import React, { useState, useEffect, useCallback } from 'react';
import { 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { 
  getAutomationApprovals, 
  approveAutomation, 
  rejectAutomation 
} from '../services/automationApi';
import { AutomationApproval, StageSelectionData } from '../types/Automation';
import AutomationApprovalModal from './AutomationApprovalModal';
import TaskCompletionStageModal from './TaskCompletionStageModal';
import { useBranch } from '../contexts/BranchContext';
import { taskApi } from '../services/taskApi';

interface AutomationApprovalsProps {
  onRefresh?: () => void;
}

const AutomationApprovals: React.FC<AutomationApprovalsProps> = ({ onRefresh }) => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [approvals, setApprovals] = useState<AutomationApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [selectedApproval, setSelectedApproval] = useState<AutomationApproval | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  
  // Stage selection state
  const [stageSelections, setStageSelections] = useState<StageSelectionData[]>([]);
  const [stageSelectionsLoading, setStageSelectionsLoading] = useState(true);
  const [selectedStageSelection, setSelectedStageSelection] = useState<StageSelectionData | null>(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [stageActionLoading, setStageActionLoading] = useState(false);

  const loadApprovals = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAutomationApprovals({ 
        page, 
        limit: 20,
        status: 'pending',
        sortBy: 'created_at',
        sortOrder: 'ASC'
      });
      
      setApprovals(response.approvals || []);
      setPagination(response.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        hasNextPage: false,
        hasPrevPage: false
      });
    } catch (err: any) {
      console.error('Error loading approvals:', err);
      setError(err.message || 'Failed to load approvals');
      setApprovals([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStageSelections = useCallback(async () => {
    try {
      setStageSelectionsLoading(true);
      const response = await taskApi.getAllPendingStageSelections();
      if (response.success) {
        setStageSelections(response.data || []);
      }
    } catch (err: any) {
      console.error('Error loading stage selections:', err);
      setStageSelections([]);
    } finally {
      setStageSelectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!branchLoading) {
      loadApprovals();
      loadStageSelections();
    }
  }, [loadApprovals, loadStageSelections, currentBranch, isViewingAllBranches, branchLoading]);

  const handleApprove = async (approvalId: number) => {
    try {
      setActionLoading(approvalId);
      await approveAutomation(approvalId);
      await loadApprovals();
      onRefresh?.();
      setSelectedApproval(null);
    } catch (err: any) {
      console.error('Error approving automation:', err);
      setError(err.message || 'Failed to approve automation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (approvalId: number) => {
    try {
      setActionLoading(approvalId);
      await rejectAutomation(approvalId);
      await loadApprovals();
      onRefresh?.();
      setSelectedApproval(null);
    } catch (err: any) {
      console.error('Error rejecting automation:', err);
      setError(err.message || 'Failed to reject automation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenStageSelection = (selection: StageSelectionData) => {
    setSelectedStageSelection(selection);
    setShowStageModal(true);
  };

  const handleStageSelect = async (selectedStageId: number) => {
    if (!selectedStageSelection || !selectedStageSelection.taskId) return;
    
    setStageActionLoading(true);
    try {
      const response = await taskApi.completeWithStageSelection(
        selectedStageSelection.taskId,
        selectedStageSelection.selectionId,
        selectedStageId
      );
      
      if (response.success) {
        setShowStageModal(false);
        setSelectedStageSelection(null);
        await loadStageSelections();
        onRefresh?.();
      } else {
        throw new Error(response.message || 'Failed to update lead stage');
      }
    } catch (err: any) {
      console.error('Error selecting stage:', err);
      throw err;
    } finally {
      setStageActionLoading(false);
    }
  };

  const handleCancelStageSelection = async (selection: StageSelectionData) => {
    if (!selection.taskId) return;
    
    try {
      await taskApi.cancelStageSelection(selection.taskId, selection.selectionId);
      await loadStageSelections();
      onRefresh?.();
    } catch (err: any) {
      console.error('Error cancelling stage selection:', err);
    }
  };

  const handleCloseStageModal = () => {
    setShowStageModal(false);
    setSelectedStageSelection(null);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading approvals...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const hasApprovals = approvals && approvals.length > 0;
  const hasStageSelections = stageSelections && stageSelections.length > 0;
  const totalPending = (pagination.totalRecords || 0) + (stageSelections?.length || 0);

  if (!hasApprovals && !hasStageSelections && !loading && !stageSelectionsLoading) {
    return (
      <div className="text-center py-8">
        <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No pending actions</h3>
        <p className="mt-1 text-sm text-gray-500">
          All automation rules and stage selections have been processed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Pending Actions</h3>
          <p className="text-sm text-gray-500">
            {totalPending} item{totalPending !== 1 ? 's' : ''} waiting for your action
          </p>
        </div>
        <button
          onClick={() => {
            loadApprovals();
            loadStageSelections();
          }}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowPathIcon className="h-4 w-4 mr-1" />
          Refresh
        </button>
      </div>

      {/* Pending Stage Selections Section */}
      {(hasStageSelections || stageSelectionsLoading) && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center">
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-orange-500 rounded-full mr-2">
              {stageSelections.length}
            </span>
            Stage Selection Required
          </h4>
          
          {stageSelectionsLoading ? (
            <div className="flex items-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
              <span className="ml-2 text-sm text-gray-500">Loading...</span>
            </div>
          ) : (
            <div className="space-y-2">
              {stageSelections.map((selection) => (
                <div 
                  key={selection.selectionId} 
                  className="bg-orange-50 border border-orange-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <ClockIcon className="h-5 w-5 text-orange-500" />
                        <span className="text-sm font-medium text-gray-900">
                          Task "{selection.taskName}" completed
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1 ml-7">
                        <div>
                          <span className="font-medium">Lead:</span>
                          <span className="ml-2">{selection.leadName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Choose stage:</span>
                          <span 
                            className="px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ 
                              backgroundColor: selection.primaryStage.color ? `${selection.primaryStage.color}20` : '#dcfce7',
                              color: selection.primaryStage.color || '#22c55e'
                            }}
                          >
                            {selection.primaryStage.name}
                          </span>
                          <span className="text-gray-400">or</span>
                          <span 
                            className="px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ 
                              backgroundColor: selection.alternateStage.color ? `${selection.alternateStage.color}20` : '#fee2e2',
                              color: selection.alternateStage.color || '#ef4444'
                            }}
                          >
                            {selection.alternateStage.name}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleCancelStageSelection(selection)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Skip
                      </button>
                      <button
                        onClick={() => handleOpenStageSelection(selection)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-orange-600 hover:bg-orange-700"
                      >
                        Select Stage
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Divider if both sections exist */}
      {hasStageSelections && hasApprovals && (
        <div className="border-t border-gray-200" />
      )}

      {/* Automation Approvals Section */}
      {hasApprovals && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center">
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-yellow-500 rounded-full mr-2">
              {pagination.totalRecords}
            </span>
            Task Automation Approvals
          </h4>

          {/* Approvals List */}
          <div className="space-y-2">
            {approvals.map((approval) => (
              <div key={approval.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getStatusIcon(approval.status)}
                      <span className="text-sm font-medium text-gray-900">
                        {approval.ruleName || 'Unknown Rule'}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(approval.status)}`}>
                        {approval.status}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1 ml-7">
                      <div>
                        <span className="font-medium">Lead:</span>
                        <span className="ml-2">
                          {approval.leadFirstName && approval.leadLastName 
                            ? `${approval.leadFirstName} ${approval.leadLastName}`
                            : 'Unknown Lead'
                          }
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Task:</span>
                        <span className="ml-2">{approval.approvalData?.taskName || 'Unknown Task'}</span>
                      </div>
                      <div>
                        <span className="font-medium">Created:</span>
                        <span className="ml-2">{formatDate(approval.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => setSelectedApproval(approval)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View
                    </button>
                    
                    <button
                      onClick={() => handleReject(approval.id)}
                      disabled={actionLoading === approval.id}
                      className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircleIcon className="h-4 w-4 mr-1" />
                      {actionLoading === approval.id ? '...' : 'Reject'}
                    </button>
                    
                    <button
                      onClick={() => handleApprove(approval.id)}
                      disabled={actionLoading === approval.id}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      {actionLoading === approval.id ? '...' : 'Approve'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-b-lg">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => loadApprovals(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => loadApprovals(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{pagination.currentPage}</span> of{' '}
                    <span className="font-medium">{pagination.totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                    <button
                      onClick={() => loadApprovals(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => loadApprovals(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Approval Modal */}
      <AutomationApprovalModal
        approval={selectedApproval}
        onApprove={() => selectedApproval && handleApprove(selectedApproval.id)}
        onReject={() => selectedApproval && handleReject(selectedApproval.id)}
        onClose={() => setSelectedApproval(null)}
        loading={actionLoading === selectedApproval?.id}
      />

      {/* Stage Selection Modal */}
      {selectedStageSelection && (
        <TaskCompletionStageModal
          isOpen={showStageModal}
          onClose={handleCloseStageModal}
          onSelectStage={handleStageSelect}
          selectionData={selectedStageSelection}
          isLoading={stageActionLoading}
        />
      )}
    </div>
  );
};

export default AutomationApprovals;
